import type * as types from './types';

import * as constants from './constants';

/**
 * A class that is used to expose methods for logging directly via the traditional {@link console}
 * object when inside of mechanics internal to the {@link Logger} or {@link Strategy} instances.
 */
export abstract class LogInternally {
  protected abstract internalMessagePrefix: string;

  protected logInternally(level: types.LogLevel, error: unknown, message: string): void;

  protected logInternally(level: types.LogLevel, message: string): void;

  /**
   * A method that issues the provided log message, {@link string}, via the traditional
   * {@link console} object, for purposes of identifying errors or warnings that may occur during
   * the operation of the {@link Strategy} instance, internally to the {@link Strategy} instance.
   *
   * This method should never be exposed externally to the {@link Strategy} instance, and is only
   * used for purposes internal to the {@link Strategy} instance itself.  This method exists because
   * the {@link Strategy} instance, in some cases, needs to issue these errors or warnings without
   * being able to call it's own 'log' method.
   *
   * @param {types.LogLevel} level
   *   The level, {@link types.LogLevel}, associated with the method on the {@link console} object
   *   that should be used.
   */
  protected logInternally(level: types.LogLevel, error: unknown, message?: string) {
    const msg = typeof error === 'string' ? error : message;
    const err = typeof error === 'string' ? undefined : error;
    const writer = constants.ConsoleWriters[level];
    const formattedMessage = msg ? `[${level}] [${this.internalMessagePrefix}]: ${msg}` : undefined;

    if (formattedMessage && err) {
      writer(err, formattedMessage);
    } else if (formattedMessage) {
      writer(formattedMessage);
    } else if (err) {
      writer(err);
    }
  }
}
